import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import cloud from 'd3-cloud';
import { AnimatePresence, motion } from 'framer-motion';

interface Word extends cloud.Word {
  text: string;
  size: number;
  color: string;
  weight: string;
  rating: number;
}

interface D3CloudComponentProps {
  technologies: string[];
  getTechSize: (tech: string, index: number) => string;
  config?: {
    about: {
      skills: Array<{ name: string; rating: number; category: string }>;
    };
  };
}

const COLORS = [
  '#6366f1', // indigo
  '#a855f7', // purple
  '#2563eb', // blue
  '#0891b2', // cyan
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#059669', // emerald
  '#ea580c', // orange
];

const D3CloudComponent: React.FC<D3CloudComponentProps> = ({
  technologies,
  getTechSize,
  config,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 560 });
  const [hoveredTech, setHoveredTech] = useState<string | null>(null);

  // Keep the SVG sized to its container so the cloud stays responsive.
  useEffect(() => {
    const measure = () => {
      const width = containerRef.current?.clientWidth || 800;
      const height = Math.max(420, Math.min(600, Math.round(width * 0.75)));
      setDimensions({ width, height });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const getSizeFromClass = useCallback((sizeClass: string): number => {
    if (sizeClass.includes('text-4xl') || sizeClass.includes('text-5xl')) return 46;
    if (sizeClass.includes('text-2xl') || sizeClass.includes('text-3xl')) return 32;
    return 21;
  }, []);

  const getRating = useCallback(
    (tech: string): number =>
      config?.about.skills.find((s) => s.name === tech)?.rating ?? 3,
    [config]
  );

  useEffect(() => {
    if (!technologies.length || !svgRef.current) return;
    const { width, height } = dimensions;

    const words: Word[] = technologies.map((tech, i) => {
      const rating = getRating(tech);
      return {
        text: tech,
        size: getSizeFromClass(getTechSize(tech, i)),
        rating,
        color: COLORS[tech.charCodeAt(0) % COLORS.length],
        weight: rating >= 4 ? '700' : '500',
      };
    });

    // d3-cloud computes a tight, non-overlapping, cloud-shaped layout via an
    // archimedean spiral. Words are kept horizontal for readability.
    const layout = cloud<Word>()
      .size([width, height])
      .words(words)
      .padding(9)
      .rotate(0)
      .font('Inter, system-ui, sans-serif')
      .fontWeight((d) => d.weight)
      .fontSize((d) => d.size)
      .spiral('archimedean')
      .on('end', render);

    layout.start();

    function render(placed: Word[]) {
      const svg = d3.select(svgRef.current!);
      svg.selectAll('*').remove();
      // Origin at the centre so d3-cloud's centre-relative coords map directly.
      const g = svg
        .append('g')
        .attr('transform', `translate(${width / 2},${height / 2})`);

      const texts = g
        .selectAll<SVGTextElement, Word>('text')
        .data(placed)
        .join('text')
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('transform', (d) => `translate(${d.x},${d.y})`)
        .style('font-family', 'Inter, system-ui, sans-serif')
        .style('font-size', (d) => `${d.size}px`)
        .style('font-weight', (d) => d.weight)
        .style('fill', (d) => d.color)
        .style('cursor', 'grab')
        .style('user-select', 'none')
        .text((d) => d.text);

      // Fade in on mount.
      texts
        .style('opacity', 0)
        .transition()
        .duration(500)
        .delay((_, i) => i * 25)
        .style('opacity', 1);

      // Hover highlight — d3 v7 passes (event, datum).
      texts
        .on('mouseover', function (_event, d) {
          setHoveredTech(d.text);
          d3.select(this)
            .raise()
            .transition()
            .duration(150)
            .style('font-size', `${d.size * 1.2}px`)
            .style('filter', 'drop-shadow(0 0 10px rgba(99, 102, 241, 0.7))');
        })
        .on('mouseout', function (_event, d) {
          setHoveredTech(null);
          d3.select(this)
            .transition()
            .duration(150)
            .style('font-size', `${d.size}px`)
            .style('filter', 'none');
        });

      // Drag repositions the word and leaves it exactly where dropped — no
      // physics, so the motion is 1:1 with the pointer and never snaps back.
      const drag = d3
        .drag<SVGTextElement, Word>()
        .subject((_event, d) => ({ x: d.x ?? 0, y: d.y ?? 0 }))
        .on('start', function () {
          d3.select(this).raise().style('cursor', 'grabbing');
        })
        .on('drag', function (event, d) {
          d.x = event.x;
          d.y = event.y;
          d3.select(this).attr('transform', `translate(${d.x},${d.y})`);
        })
        .on('end', function () {
          d3.select(this).style('cursor', 'grab');
        });

      texts.call(drag);
    }

    return () => {
      layout.stop();
    };
  }, [technologies, dimensions, getTechSize, getRating, getSizeFromClass]);

  const hoveredSkill = config?.about.skills.find((s) => s.name === hoveredTech);

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        ref={svgRef}
        width={dimensions.width}
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        className="w-full h-auto touch-none"
        style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.08))' }}
      />

      {/* Hover tooltip */}
      <AnimatePresence>
        {hoveredTech && (
          <motion.div
            key={hoveredTech}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute top-4 left-1/2 -translate-x-1/2 bg-white dark:bg-dark-800 px-4 py-2 rounded-lg shadow-xl text-base font-semibold z-30 border border-primary-200 dark:border-primary-800 pointer-events-none"
          >
            {hoveredTech}
            {hoveredSkill && (
              <div className="flex items-center justify-center mt-1 space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className={`w-3 h-3 ${
                      star <= hoveredSkill.rating
                        ? 'text-primary-500 fill-current'
                        : 'text-gray-300 dark:text-gray-600'
                    }`}
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Instructions */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-center text-sm text-dark-500 dark:text-dark-400 pointer-events-none">
        <p>Hover to highlight · Drag to move</p>
      </div>
    </div>
  );
};

export default D3CloudComponent;
